import { Dispatch, RefObject, SetStateAction, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Descendant } from 'slate'
import { mutate } from 'swr'
import { ApiPaginationResponseInterface } from 'shared/types/api-pagination-response-interface'
import { AttachmentsRefInterface } from 'modules/attachments/components/context'
import { useAddComment } from 'modules/comments/api/add-comment'
import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import { useCommentHighlight } from 'modules/comments/contexts/comment-highlight'
import { OnErrorType } from 'modules/comments/types'
import { isNewestFirst } from 'modules/comments/utils/comment-sorting'
import {
  isCommunityExtra,
  isLightLectureExtra,
} from 'modules/comments/utils/comments-extra-type-guards'
import {
  getLastCommentId,
  insertCommentToLinkedList,
} from 'modules/comments/utils/comments-linked-list'
import { getCommentsCountKey } from 'modules/community/api/get-comments-count'
import { communityKeys } from 'modules/community/api/key-factory'
import { PostAttachmentType } from 'modules/community/types/post-attachment'
import { PostCommentsInfoInterface } from 'modules/community/types/post-info'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'

const updateResponsesCount = (
  commentList: CommentInterface[],
  parentId: number,
): CommentInterface[] =>
  commentList.map(comment =>
    comment.id === parentId ? { ...comment, totalReplies: comment.totalReplies + 1 } : comment,
  )

export function useCommentBox({
  parentId,
  level = 0,
  setActiveComment = () => {},
  setOpenedResponses = () => {},
  onError,
  replyInfo,
}: {
  parentId: number | null
  level?: number
  setActiveComment?: (comment: CommentInterface | null) => void
  setOpenedResponses?: (value: boolean) => void
  onError?: OnErrorType
  replyInfo?: { userId: number; displayName: string; profileImage: string }
}): {
  handleAddComment: (textHtml: string) => Promise<void>
  attachmentsRef: RefObject<AttachmentsRefInterface>
  attachments: PostAttachmentType[]
  setAttachments: Dispatch<SetStateAction<PostAttachmentType[]>>
  initialValue: Descendant[] | undefined
} {
  const { setComments, commentsList, extra, repliesList, updateReplies, currentSortOption } =
    useCommentsContext()
  const { setHighlightedComment } = useCommentHighlight()
  const queryClient = useQueryClient()
  const parentIRI = parentId ? `/api/comment/comments/${parentId}` : null
  const attachmentsRef = useRef<AttachmentsRefInterface>(null)
  const [attachments, setAttachments] = useState<PostAttachmentType[]>([])
  const addCommentMutation = useAddComment({})

  const isCommunityComments = isCommunityExtra(extra)
  const isLightLecture = isLightLectureExtra(extra)

  const initialValue: Descendant[] | undefined = replyInfo
    ? [
        {
          type: 'paragraph',
          children: [
            {
              type: 'mention',
              displayName: replyInfo.displayName,
              id: replyInfo.userId,
              profileImageUrl: replyInfo.profileImage,
              children: [{ text: '' }],
            },
            {
              text: ', ',
            },
          ],
        },
      ]
    : undefined

  async function handleAddComment(textHtml: string): Promise<void> {
    try {
      const commentSource = isCommunityComments
        ? {
            commentSourceCommunityPost: {
              communityPostId: extra.community.postId,
            },
          }
        : isLightLecture
          ? {
              commentSourceLightLecture: {
                lectureId: extra.lightLecture.lectureId,
              },
            }
          : {
              commentSourcePage: {
                pageId: extra.course.pageId,
              },
            }
      const comment = {
        text: textHtml,
        parent: parentIRI,
        ...commentSource,
        attachments: escapeAttachmentLocalFields(attachments),
      }
      const data = await addCommentMutation.trigger(comment)
      const commentWithFlag: CommentInterface = { ...data, __isNew: true, nextId: null }

      if (parentId === null) {
        setComments(prevComments =>
          insertCommentToLinkedList(
            prevComments,
            commentWithFlag,
            isNewestFirst(currentSortOption) ? null : getLastCommentId(prevComments),
          ),
        )
      } else {
        const updatedCommentList = updateResponsesCount(commentsList, parentId)
        setComments(updatedCommentList)
        if (level === 0) {
          setOpenedResponses(true)
        }

        updateReplies({
          commentId: parentId,
          replies: insertCommentToLinkedList(
            repliesList[parentId].replies,
            commentWithFlag,
            getLastCommentId(repliesList[parentId].replies),
          ),
        })
      }
      if (isCommunityComments) {
        setHighlightedComment(commentWithFlag)
      }
      setActiveComment(null)
      if (isCommunityComments) {
        mutate(getCommentsCountKey(extra.community.communityPath, [extra.community.postId]))
        queryClient.setQueriesData<ApiPaginationResponseInterface<PostCommentsInfoInterface>>(
          { queryKey: communityKeys.postsCommentsGeneral() },
          oldData => {
            const info = oldData?.items.find(item => item.postId === extra.community.postId)

            if (!oldData || !info) return oldData

            return {
              ...oldData,
              items: oldData.items.map(item =>
                item.postId === extra.community.postId
                  ? {
                      ...item,
                      numberOfComments: item.numberOfComments + 1,
                    }
                  : item,
              ),
            }
          },
        )
        queryClient.invalidateQueries({ queryKey: communityKeys.postsCommentsGeneral() })
      }
      attachmentsRef.current?.clear()
    } catch (error) {
      if (onError) onError(error)
    }
  }

  return {
    handleAddComment,
    attachmentsRef,
    attachments,
    setAttachments,
    initialValue,
  }
}
